/**
 * Phase 15 — Inline "New Goldsmith" dialog.
 *
 * Reusable lightweight dialog that lets any page create a goldsmith on
 * the fly without leaving the current flow (matches the existing
 * inline-purity / inline-vendor patterns from Phase 12 / Phase 10).
 *
 * Props:
 *   open, onOpenChange  — controlled dialog state
 *   companyId           — required for tenant-scoped create
 *   onCreated(goldsmith) — invoked with the new goldsmith document on
 *                          success; caller is responsible for refreshing
 *                          its goldsmith list / auto-selecting.
 */
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Hammer } from 'lucide-react';

export function GoldsmithFormDialog({ open, onOpenChange, companyId, onCreated }) {
  const empty = {
    name: '', contact_person: '', phone: '', email: '',
    address: '', specialization: '', notes: '',
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Goldsmith name is required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/masters/goldsmiths', {
        ...form,
        company_id: companyId,
      });
      toast.success(`Goldsmith "${data.name}" created`);
      if (onCreated) onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to create goldsmith'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inline-goldsmith-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--ink-navy)]">
            <Hammer size={16} /> New Goldsmith
          </DialogTitle>
          <DialogDescription>
            Add an artisan/karigar to your manufacturing network. You can update
            the details later from the Goldsmith master.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Ramesh Soni"
              data-testid="inline-goldsmith-name-input"
              autoFocus
            />
          </div>
          <div>
            <Label>Contact Person</Label>
            <Input
              value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
              data-testid="inline-goldsmith-contact-input"
            />
          </div>
          <div>
            <Label>Specialization</Label>
            <Input
              value={form.specialization}
              onChange={(e) => setForm({ ...form, specialization: e.target.value })}
              placeholder="e.g. Setting, Polish"
              data-testid="inline-goldsmith-spec-input"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="inline-goldsmith-phone-input"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="inline-goldsmith-email-input"
            />
          </div>
          <div className="col-span-2">
            <Label>Address</Label>
            <Textarea
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              rows={2}
              data-testid="inline-goldsmith-address-input"
            />
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              data-testid="inline-goldsmith-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="inline-goldsmith-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            className="luxury-button"
            disabled={saving || !form.name.trim()}
            data-testid="inline-goldsmith-submit-button"
          >
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default GoldsmithFormDialog;
